//#region src/checker.ts
/**
* Checks if the given value is an object.
*/
function isObject(value) {
	return value != null && typeof value === "object";
}
/**
* Checks if the given value is a Map.
*/
function isMap(value) {
	return value instanceof Map;
}
/**
* Checks if the given value is a Set.
*/
function isSet(value) {
	return value instanceof Set;
}

//#endregion
//#region src/default-map.ts
/**
* A map that automatically creates values for missing keys using a factory function.
*
* Similar to Python's [defaultdict](https://docs.python.org/3.13/library/collections.html#collections.defaultdict).
*/
var DefaultMap = class extends Map {
	constructor(defaultFactory, iterable) {
		super(iterable);
		this.defaultFactory = defaultFactory;
	}
	get(key) {
		if (this.has(key)) return super.get(key);
		const value = this.defaultFactory();
		this.set(key, value);
		return value;
	}
};

//#endregion
//#region src/dom-node-type.ts
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const DOCUMENT_NODE = 9;
const DOCUMENT_FRAGMENT_NODE = 11;

//#endregion
//#region src/dom.ts
/**
* Checks if the given DOM node is an Element.
*/
function isElement(node) {
	return node.nodeType === ELEMENT_NODE;
}
/**
* Checks if the given DOM node is a Text node.
*/
function isTextNode(node) {
	return node.nodeType === TEXT_NODE;
}
/**
* Checks if the given DOM node is an HTMLElement.
*/
function isHTMLElement(node) {
	return isElement(node) && node.namespaceURI === "http://www.w3.org/1999/xhtml";
}
/**
* Checks if the given DOM node is an SVGElement.
*/
function isSVGElement(node) {
	return isElement(node) && node.namespaceURI === "http://www.w3.org/2000/svg";
}
/**
* Checks if the given DOM node is an MathMLElement.
*/
function isMathMLElement(node) {
	return isElement(node) && node.namespaceURI === "http://www.w3.org/1998/Math/MathML";
}
/**
* Checks if the given DOM node is a Document.
*/
function isDocument(node) {
	return node.nodeType === DOCUMENT_NODE;
}
/**
* Checks if the given DOM node is a DocumentFragment.
*/
function isDocumentFragment(node) {
	return node.nodeType === DOCUMENT_FRAGMENT_NODE;
}
/**
* Checks if the given DOM node is a ShadowRoot.
*/
function isShadowRoot(node) {
	return isDocumentFragment(node) && "host" in node && isElementLike(node.host);
}
/**
* Checks if an unknown value is likely a DOM node.
*/
function isNodeLike(value) {
	return isObject(value) && value.nodeType !== void 0;
}
/**
* Checks if an unknown value is likely a DOM element.
*/
function isElementLike(value) {
	return isObject(value) && value.nodeType === ELEMENT_NODE && typeof value.nodeName === "string";
}
/**
* Checks if the given value is likely a Window object.
*/
function isWindowLike(value) {
	return isObject(value) && value.window === value;
}
/**
* Gets the window object for the given target or the global window object if no
* target is provided.
*/
function getWindow(target) {
	if (target) {
		if (isShadowRoot(target)) return getWindow(target.host);
		if (isDocument(target)) return target.defaultView || window;
		if (isElement(target)) return target.ownerDocument?.defaultView || window;
	}
	return window;
}
/**
* Gets the document for the given target or the global document if no target is
* provided.
*/
function getDocument(target) {
	if (target) {
		if (isWindowLike(target)) return target.document;
		if (isDocument(target)) return target;
		return target.ownerDocument || document;
	}
	return document;
}
/**
* Gets a reference to the root node of the document based on the given target.
*/
function getDocumentElement(target) {
	return getDocument(target).documentElement;
}

//#endregion
//#region src/format-bytes.ts
/**
* Formats a number of bytes into a human-readable string.
* @param bytes - The number of bytes to format.
* @returns A string representing the number of bytes in a human-readable format.
*/
function formatBytes(bytes) {
	const units = [
		"B",
		"KB",
		"MB",
		"GB"
	];
	let unitIndex = 0;
	let num = bytes;
	while (Math.abs(num) >= 1024 && unitIndex < units.length - 1) {
		num /= 1024;
		unitIndex++;
	}
	const fraction = unitIndex === 0 && num % 1 === 0 ? 0 : 1;
	return `${num.toFixed(fraction)}${units[unitIndex]}`;
}

//#endregion
//#region src/get-id.ts
let id = 0;
/**
* Generates a unique positive integer.
*/
function getId() {
	id = id % Number.MAX_SAFE_INTEGER + 1;
	return id;
}

//#endregion
//#region src/once.ts
/**
* Creates a function that will only execute the provided function once.
* Subsequent calls will return the cached result from the first execution.
*
* @param fn The function to execute once
* @returns A function that will only execute the provided function once
* @example
* ```ts
* const getValue = once(() => expensiveOperation())
* getValue() // executes expensiveOperation
* getValue() // returns cached result
* ```
*/
function once(fn) {
	let called = false;
	let result;
	return () => {
		if (!called) {
			result = fn();
			called = true;
			fn = void 0;
		}
		return result;
	};
}

//#endregion
//#region src/sleep.ts
/**
* Sleep for a given number of milliseconds.
*/
function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

//#endregion
//#region src/is-deep-equal.ts
/**
* Whether two values are deeply equal.
*/
function isDeepEqual(a, b) {
	if (a === b) return true;
	if (a == null || b == null) return false;
	const aType = typeof a;
	if (aType !== typeof b) return false;
	if (aType === "number" && Number.isNaN(a) && Number.isNaN(b)) return true;
	if (Array.isArray(a)) {
		if (!Array.isArray(b)) return false;
		if (a.length !== b.length) return false;
		const size = a.length;
		for (let i = 0; i < size; i++) if (!isDeepEqual(a[i], b[i])) return false;
		return true;
	}
	if (isSet(a)) {
		if (!isSet(b)) return false;
		if (a.size !== b.size) return false;
		for (const value of a) if (!b.has(value)) return false;
		return true;
	}
	if (isMap(a)) {
		if (!isMap(b)) return false;
		if (a.size !== b.size) return false;
		for (const key of a.keys()) {
			if (!b.has(key)) return false;
			if (!isDeepEqual(a.get(key), b.get(key))) return false;
		}
		return true;
	}
	if (typeof a === "object" && typeof b === "object") {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;
		for (const key of aKeys) if (!isDeepEqual(a[key], b[key])) return false;
		return true;
	}
	return false;
}

//#endregion
export { DefaultMap, formatBytes, getDocument, getDocumentElement, getId, getWindow, isDeepEqual, isDocument, isDocumentFragment, isElement, isElementLike, isHTMLElement, isMap, isMathMLElement, isNodeLike, isObject, isSVGElement, isSet, isShadowRoot, isTextNode, isWindowLike, once, sleep };
//# sourceMappingURL=index.js.map